// Devices: the fleet with its health, one device (settings, probes, calibrations), and the claim wizard
// (code from the label → the device comes online → its probes are assigned to assets). Power meters show among
// the probes (read by a gateway, or a Shelly connected directly: see Meters.tsx). While a device's page is open,
// the device is in live mode (lib/deviceLive.ts): its probes' values arrive every 10 s.

import type { Device, EnergyMeter, Probe } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { LiveBadge, LiveValue } from '../components/LiveProbe.tsx';
import { useCurrentOrg } from '../components/Shell.tsx';
import {
  Alert, Badge, Button, Card, EmptyState, Field, Input, LinkButton, Modal, PageHeader, Select, Spinner, Table, Td,
} from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { ApiError, uploadFile } from '../lib/api.ts';
import { type DeviceLive, useDeviceLive } from '../lib/deviceLive.ts';
import { formatAgo, formatDate, formatDateTime, formatNumber, formatTemp } from '../lib/format.ts';
import {
  orgApi, refreshScoped, useAssets, useCalibrations, useDevice, useProbeFeeds, useDevices, useEnergySettings, useMeters, useOrgMutation, useProbes,
  useSites,
} from '../lib/queries.ts';
import { DirectShellyDialog, MeterDialog, RotateAddressDialog } from './Meters.tsx';

export function HealthBadge({ d }: { d: Device }) {
  const { t } = useI18n();
  if (d.status === 'pending_token') return <Badge tone="amber">{t('devices.pending')}</Badge>;
  if (d.status === 'revoked') return <Badge tone="red">{t('devices.revoked')}</Badge>;
  if (d.status === 'released') return <Badge tone="slate">{t('devices.released')}</Badge>;
  return d.health.online ? <Badge tone="green">{t('devices.online')}</Badge> : <Badge tone="violet">{t('devices.offline')}</Badge>;
}

export function Devices() {
  const { orgId, can } = useCurrentOrg();
  const { t, lang } = useI18n();
  const devices = useDevices(orgId);
  const sites = useSites(orgId);
  const energy = useEnergySettings(orgId).data?.features.energy ?? false;
  const [shelly, setShelly] = useState(false);
  const siteName = (id: string | null) => sites.data?.data.find((s) => s.id === id)?.name ?? '—';
  return (
    <div>
      <PageHeader title={t('devices.title')} actions={can('admin') && (
        <>
          {energy && <Button variant="secondary" onClick={() => setShelly(true)}>{t('meters.shelly.add')}</Button>}
          <LinkButton variant="secondary" to="../lora">{t('lora.title')}</LinkButton>
          <LinkButton to="claim">{t('devices.claim')}</LinkButton>
        </>
      )} />
      {shelly && <DirectShellyDialog onClose={() => setShelly(false)} />}
      {devices.isPending && <Spinner />}
      {devices.data?.data.length === 0 && (
        <EmptyState title={t('devices.none')} action={can('admin') && <LinkButton to="claim">{t('devices.claim')}</LinkButton>}>
          {t('devices.noneHint')}
        </EmptyState>
      )}
      {(devices.data?.data.length ?? 0) > 0 && (
        <Card>
          <Table head={[t('devices.device'), t('devices.site'), t('devices.state'), t('devices.lastSeen'), t('devices.battery'),
            t('devices.signal'), t('devices.firmware')]}>
            {devices.data!.data.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50">
                <Td><Link to={d.id} className="font-medium text-brand-700 hover:underline">{d.label ?? d.hardware_id}</Link>
                  {d.label && <span className="block font-mono text-xs text-slate-500">{d.hardware_id}</span>}</Td>
                <Td>{siteName(d.site_id)}</Td>
                <Td><HealthBadge d={d} /></Td>
                <Td>{formatAgo(d.health.last_seen_at, lang)}</Td>
                <Td className="tabular-nums">{d.health.mains ? t('devices.mains') : d.health.battery_v !== null
                  ? `${formatNumber(d.health.battery_v, lang, 2)} V` : '—'}</Td>
                <Td className="tabular-nums">{d.health.rssi_dbm !== null ? `${d.health.rssi_dbm} dBm` : '—'}</Td>
                <Td className="font-mono text-xs">{d.health.fw_version ?? '—'}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- one device

export function DeviceDetail() {
  const { deviceId = '' } = useParams();
  const { orgId, can } = useCurrentOrg();
  const { t, lang } = useI18n();
  const device = useDevice(orgId, deviceId);
  const sites = useSites(orgId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<'revoke' | 'release' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const update = useOrgMutation(orgId, (b: Record<string, unknown>) => orgApi(orgId).updateDevice(deviceId, b), ['device', 'devices']);
  const [form, setForm] = useState<{ label: string; site_id: string; sample: string; upload: string } | null>(null);
  const [rotate, setRotate] = useState(false);
  const direct = device.data?.hardware_id.startsWith('SHELLY-') ?? false;
  const temperature = (device.data?.probes ?? []).filter((p) => p.kind === 'temperature' && !p.disabled_at).map((p) => p.id);
  const live = useDeviceLive(orgId, deviceId, temperature, device.data?.status === 'active' && !direct && temperature.length > 0);
  // A probe the device reported meanwhile (it comes unassigned): list it.
  useEffect(() => {
    if (live.unknownProbe > 0) void refreshScoped(qc, orgId, ['device']);
  }, [live.unknownProbe, qc, orgId]);

  if (device.isPending) return <Spinner />;
  if (device.isError) {
    return <Alert tone="red">{device.error instanceof ApiError && device.error.status === 404 ? t('devices.notFound') : t('errors.unavailable')}</Alert>;
  }
  const d = device.data;
  const ended = d.status === 'released' || d.status === 'revoked';

  const end = async () => {
    setError(null);
    try {
      if (confirm === 'revoke') await orgApi(orgId).revokeDevice(d.id);
      else await orgApi(orgId).releaseDevice(d.id);
      setConfirm(null);
      await refreshScoped(qc, orgId, ['devices', 'device', 'probes', 'overview']);
      if (confirm === 'release') navigate(`/o/${orgId}/devices`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setError(null);
    try {
      await update.mutateAsync({ label: form.label || null, ...(form.site_id ? { site_id: form.site_id } : {}),
        settings: { sample_interval_s: Number(form.sample), upload_interval_s: Number(form.upload) } });
      setForm(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={<span className="flex items-center gap-3">{d.label ?? d.hardware_id} <HealthBadge d={d} /></span>}
        subtitle={<span className="font-mono">{d.hardware_id}</span>}
        actions={can('admin') && !ended && (
          <>
            <Button variant="secondary" onClick={() => setForm({ label: d.label ?? '', site_id: d.site_id ?? '',
              sample: String(d.settings.sample_interval_s ?? 60), upload: String(d.settings.upload_interval_s ?? 300) })}>
              {t('common.edit')}
            </Button>
            {direct && <Button variant="secondary" onClick={() => setRotate(true)}>{t('meters.shelly.newAddress')}</Button>}
            <Button variant="secondary" onClick={() => setConfirm('release')}>{t('devices.release')}</Button>
            <Button variant="danger" onClick={() => setConfirm('revoke')}>{t('devices.revoke')}</Button>
          </>
        )} />
      {error && <Alert tone="red">{error}</Alert>}
      {d.status === 'pending_token' && <Alert tone="amber" title={t('devices.waiting')}>{direct ? t('meters.shelly.waiting') : t('devices.waitingHint')}</Alert>}
      {rotate && <RotateAddressDialog deviceId={d.id} onClose={() => setRotate(false)} />}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card title={t('devices.health')}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            <dt className="text-slate-500">{t('devices.lastSeen')}</dt><dd>{formatDateTime(d.health.last_seen_at, lang)}</dd>
            <dt className="text-slate-500">{t('devices.power')}</dt>
            <dd>{d.health.mains === null ? '—' : d.health.mains ? t('devices.mains') : t('devices.onBattery')}</dd>
            <dt className="text-slate-500">{t('devices.battery')}</dt><dd>{d.health.battery_v !== null ? `${formatNumber(d.health.battery_v, lang, 2)} V` : '—'}</dd>
            <dt className="text-slate-500">{t('devices.signal')}</dt><dd>{d.health.rssi_dbm !== null ? `${d.health.rssi_dbm} dBm` : '—'}</dd>
            <dt className="text-slate-500">{t('devices.firmware')}</dt><dd className="font-mono">{d.health.fw_version ?? '—'}</dd>
            <dt className="text-slate-500">{t('devices.lastReset')}</dt><dd>{d.health.reset_reason ?? '—'}</dd>
            <dt className="text-slate-500">{t('devices.claimedAt')}</dt><dd>{formatDate(d.claimed_at, lang)}</dd>
            <dt className="text-slate-500">{t('devices.site')}</dt><dd>{sites.data?.data.find((s) => s.id === d.site_id)?.name ?? '—'}</dd>
            <dt className="text-slate-500">{t('devices.sampling')}</dt>
            <dd>{t('devices.samplingValue', { sample: d.settings.sample_interval_s ?? 60, upload: d.settings.upload_interval_s ?? 300 })}</dd>
            <dt className="text-slate-500">{t('devices.configVersion')}</dt><dd>{d.config_version}</dd>
          </dl>
        </Card>
        <Card title={t('devices.probes')} className="lg:col-span-2" actions={!ended && <LiveBadge live={live} />}>
          {d.probes.length === 0 ? <p className="text-sm text-slate-500">{t('devices.noProbes')}</p>
            : <ProbeTable probes={d.probes} editable={can('admin') && !ended} live={live.status === 'unavailable' ? undefined : live} />}
        </Card>
      </div>

      <Modal open={form !== null} title={t('devices.settings')} onClose={() => setForm(null)}>
        {form && (
          <form onSubmit={save} className="flex flex-col gap-4">
            <Field label={t('devices.label')}>
              {(p) => <Input {...p} value={form.label} maxLength={100} onChange={(e) => setForm({ ...form, label: e.target.value })} />}
            </Field>
            <Field label={t('devices.site')}>
              {(p) => (
                <Select {...p} value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
                  <option value="">—</option>
                  {sites.data?.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('devices.sampleEvery')} hint={t('devices.seconds')}>
                {(p) => <Input {...p} type="number" min={60} max={3600} step={60} value={form.sample}
                  onChange={(e) => setForm({ ...form, sample: e.target.value })} />}
              </Field>
              <Field label={t('devices.uploadEvery')} hint={t('devices.seconds')}>
                {(p) => <Input {...p} type="number" min={60} max={3600} step={60} value={form.upload}
                  onChange={(e) => setForm({ ...form, upload: e.target.value })} />}
              </Field>
            </div>
            <p className="text-xs text-slate-500">{t('devices.settingsHint')}</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={update.isPending}>{t('common.save')}</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={confirm !== null} title={confirm === 'revoke' ? t('devices.revokeTitle') : t('devices.releaseTitle')}
        onClose={() => setConfirm(null)} footer={(
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" onClick={end}>{confirm === 'revoke' ? t('devices.revoke') : t('devices.release')}</Button>
          </>
        )}>
        <p className="text-sm text-slate-600">{confirm === 'revoke' ? t('devices.revokeHint') : t('devices.releaseHint')}</p>
      </Modal>
    </div>
  );
}

function ProbeTable({ probes, editable, live }: { probes: Probe[]; editable: boolean; live?: DeviceLive | undefined }) {
  const { orgId } = useCurrentOrg();
  const { t, lang } = useI18n();
  const assets = useAssets(orgId);
  const hasMeters = probes.some((p) => p.kind === 'energy');
  const meters = useMeters(orgId, `?device_id=${probes[0]?.device_id ?? ''}`, hasMeters);
  const meterOf = (p: Probe): EnergyMeter | undefined => meters.data?.data.find((m) => m.probe_id === p.id);
  const [meter, setMeter] = useState<EnergyMeter | null>(null);
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [calibrate, setCalibrate] = useState<Probe | null>(null);
  const [feeds, setFeeds] = useState<Probe | null>(null);
  const assign = async (p: Probe, assetId: string) => {
    setError(null);
    try {
      if (assetId) await orgApi(orgId).assignProbe(p.id, assetId);
      else await orgApi(orgId).unassignProbe(p.id);
      await refreshScoped(qc, orgId, ['device', 'probes', 'asset', 'overview', 'energy']);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'plan_limit_exceeded' ? t(p.kind === 'energy' ? 'meters.shelly.limit' : 'devices.probeLimit')
        : (err as Error).message);
    }
  };
  const rename = async (p: Probe) => {
    const label = prompt(t('devices.probeLabel'), p.label ?? '');
    if (label === null) return;
    await orgApi(orgId).updateProbe(p.id, { label: label || null });
    await refreshScoped(qc, orgId, ['device', 'probes']);
  };
  return (
    <>
      {error && <div className="mb-3"><Alert tone="red">{error}</Alert></div>}
      <Table head={[t('devices.probe'), t('devices.lastValue'), t('devices.asset'), t('assets.calibration'), '']}>
        {probes.map((p) => (
          <tr key={p.id}>
            <Td>{p.label ?? p.channel}<span className="block font-mono text-xs text-slate-500">{p.channel} · {t(`probeKind.${p.kind}`)}</span>
              {p.kind === 'temperature' && (editable ? (
                <select aria-label={t('haccp.cooling.field.probe_role')} value={p.role ?? 'air'}
                  className="mt-1 rounded border border-slate-300 bg-white py-0.5 pl-1 pr-6 text-xs"
                  onChange={async (e) => {
                    await orgApi(orgId).updateProbe(p.id, { role: e.target.value });
                    await refreshScoped(qc, orgId, ['device', 'probes']);
                  }}>
                  <option value="air">{t('haccp.cooling.role.air')}</option>
                  <option value="core">{t('haccp.cooling.role.core')}</option>
                </select>
              ) : <span className="block text-xs text-slate-500">{t(`haccp.cooling.role.${p.role ?? 'air'}`)}</span>)}</Td>
            {p.kind === 'energy' ? (
              <Td>
                <span className="text-xs text-slate-600">{meterOf(p)?.model ?? t('probeKind.energy')}</span>
                <span className="block text-xs text-slate-500">{meterOf(p)?.last_minute_at ? formatAgo(meterOf(p)!.last_minute_at, lang) : t('meters.noData')}</span>
              </Td>
            ) : live && p.kind === 'temperature' && !p.disabled_at ? (
              <Td><LiveValue probe={live.values[p.id]} now={live.now} fallback={{ value: p.last_value_c, at: p.last_reading_at }} /></Td>
            ) : (
              <Td className="tabular-nums">{p.kind === 'door' ? '—' : formatTemp(p.last_value_c, lang)}
                {p.last_reading_at && <span className="block text-xs text-slate-500">{formatAgo(p.last_reading_at, lang)}</span>}</Td>
            )}
            <Td>
              {editable ? (
                <Select aria-label={t('devices.asset')} value={p.asset_id ?? ''} onChange={(e) => assign(p, e.target.value)}>
                  <option value="">{t('devices.unassigned')}</option>
                  {assets.data?.data.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </Select>
              ) : (assets.data?.data.find((a) => a.id === p.asset_id)?.name ?? t('devices.unassigned'))}
            </Td>
            <Td className="tabular-nums">{p.kind !== 'temperature' ? '—' : `${p.calibration_offset_c >= 0 ? '+' : ''}${formatNumber(p.calibration_offset_c, lang, 2)} °C`}</Td>
            <Td className="whitespace-nowrap text-right">
              {editable && <Button size="sm" variant="ghost" onClick={() => rename(p)}>{t('devices.rename')}</Button>}
              {editable && p.kind === 'temperature' && <Button size="sm" variant="ghost" onClick={() => setCalibrate(p)}>{t('devices.calibrate')}</Button>}
              {editable && p.kind === 'temperature' && !p.disabled_at && <Button size="sm" variant="ghost" onClick={() => setFeeds(p)}>{t('devices.feeds')}</Button>}
              {editable && p.kind === 'energy' && meterOf(p) && <Button size="sm" variant="ghost" onClick={() => setMeter(meterOf(p)!)}>{t('meters.settings')}</Button>}
            </Td>
          </tr>
        ))}
      </Table>
      {calibrate && <CalibrationDialog probe={calibrate} onClose={() => setCalibrate(null)} />}
      {feeds && <FeedsDialog probe={feeds} onClose={() => setFeeds(null)} />}
      {meter && <MeterDialog meter={meter} onClose={() => setMeter(null)} />}
    </>
  );
}

function CalibrationDialog({ probe, onClose }: { probe: Probe; onClose: () => void }) {
  const { orgId } = useCurrentOrg();
  const { t, lang } = useI18n();
  const history = useCalibrations(orgId, probe.id);
  const qc = useQueryClient();
  const [offset, setOffset] = useState('0');
  const [note, setNote] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const certificate = file ? await uploadFile(orgId, file, 'calibration_certificate') : undefined;
      await orgApi(orgId).addCalibration(probe.id, { offset_c: Number(offset.replace(',', '.')), ...(note ? { note } : {}),
        ...(validUntil ? { certificate_valid_until: validUntil } : {}), ...(certificate ? { certificate_upload_id: certificate } : {}) });
      await refreshScoped(qc, orgId, ['device', 'probes', 'calibrations']);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open title={t('devices.calibrationTitle', { probe: probe.label ?? probe.channel })} onClose={onClose} wide>
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <p className="text-sm text-slate-600 sm:col-span-2">{t('devices.calibrationHint')}</p>
        {error && <div className="sm:col-span-2"><Alert tone="red">{error}</Alert></div>}
        <Field label={t('devices.offset')} hint="−5,00 … +5,00 °C">
          {(p) => <Input {...p} inputMode="decimal" value={offset} onChange={(e) => setOffset(e.target.value)} required />}
        </Field>
        <Field label={t('devices.certificateValidUntil')}>
          {(p) => <Input {...p} type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />}
        </Field>
        <Field label={t('devices.certificate')} hint={t('devices.certificateHint')}>
          {(p) => <Input {...p} type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />}
        </Field>
        <Field label={t('devices.note')}>
          {(p) => <Input {...p} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" loading={busy}>{t('common.save')}</Button>
        </div>
      </form>
      {(history.data?.data.length ?? 0) > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 text-sm font-semibold text-slate-700">{t('devices.calibrationHistory')}</h3>
          <Table head={[t('devices.from'), t('devices.offset'), t('devices.certificate')]}>
            {history.data!.data.map((c) => (
              <tr key={c.id}>
                <Td>{formatDateTime(c.effective_from, lang)}</Td>
                <Td className="tabular-nums">{c.offset_c >= 0 ? '+' : ''}{formatNumber(c.offset_c, lang, 2)} °C</Td>
                <Td>{c.certificate_url ? <a href={c.certificate_url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">
                  {t('devices.view')}</a> : '—'}{c.certificate_valid_until && ` (${formatDate(c.certificate_valid_until, lang)})`}</Td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </Modal>
  );
}

/** A probe's feeds: secret links to its readings for tools that cannot sign in (cck-api routes/feeds.ts). A new
 *  link is shown once, with a copy button: only its hash is stored. */
function FeedsDialog({ probe, onClose }: { probe: Probe; onClose: () => void }) {
  const { orgId } = useCurrentOrg();
  const { t, lang } = useI18n();
  const feeds = useProbeFeeds(orgId, probe.id);
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await orgApi(orgId).createProbeFeed(probe.id, label.trim() ? { label: label.trim() } : {});
      setUrl(created.url);
      setCopied(false);
      setLabel('');
      await refreshScoped(qc, orgId, ['feeds']);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'too_many_feeds' ? t('devices.feedLimit') : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url!);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const revoke = async (feedId: string) => {
    if (!confirm(t('devices.feedRevokeConfirm'))) return;
    setError(null);
    try {
      await orgApi(orgId).revokeProbeFeed(probe.id, feedId);
      await refreshScoped(qc, orgId, ['feeds']);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <Modal open title={t('devices.feedsTitle', { probe: probe.label ?? probe.channel })} onClose={onClose} wide>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">{t('devices.feedsHint')}</p>
        {error && <Alert tone="red">{error}</Alert>}
        {url && (
          <div className="flex flex-col gap-2">
            <Alert tone="amber" title={t('devices.feedOnce')}>{t('devices.feedOnceHint')}</Alert>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input readOnly value={url} aria-label={t('devices.feedOnce')} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" variant="secondary" onClick={copy}>{copied ? t('devices.feedCopied') : t('devices.feedCopy')}</Button>
            </div>
            <p className="text-xs text-slate-500">{t('devices.feedFormats')}</p>
          </div>
        )}
        <form onSubmit={create} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field label={t('devices.feedLabel')} hint={t('devices.feedLabelHint')}>
              {(p) => <Input {...p} value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />}
            </Field>
          </div>
          <Button type="submit" loading={busy}>{t('devices.feedCreate')}</Button>
        </form>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">{t('devices.feedActive')}</h3>
          {feeds.isLoading ? <Spinner /> : (feeds.data?.data.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">{t('devices.feedNone')}</p>
          ) : (
            <ul className="divide-y divide-slate-200 rounded border border-slate-200">
              {feeds.data!.data.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span>
                    {f.label ?? t('devices.feeds')}
                    <span className="block text-xs text-slate-500">
                      {t('devices.feedCreated', { when: formatAgo(f.created_at, lang) })} · {f.last_used_at
                        ? t('devices.feedUsed', { when: formatAgo(f.last_used_at, lang) }) : t('devices.feedNeverUsed')}
                    </span>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => revoke(f.id)}>{t('devices.feedRevoke')}</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- claim wizard

export function ClaimDevice() {
  const { orgId } = useCurrentOrg();
  const { t } = useI18n();
  const sites = useSites(orgId);
  const qc = useQueryClient();
  const [form, setForm] = useState({ hardware_id: '', claim_code: '', label: '', site_id: '' });
  const [device, setDevice] = useState<Device | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const d = await orgApi(orgId).claimDevice({ hardware_id: form.hardware_id.trim().toUpperCase(), claim_code: form.claim_code,
        ...(form.label ? { label: form.label } : {}), ...(form.site_id ? { site_id: form.site_id } : {}) });
      setDevice(d);
      await refreshScoped(qc, orgId, ['devices']);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(code === 'not_found' ? t('claim.wrongCode') : code === 'device_already_claimed' ? t('claim.elsewhere')
        : code === 'plan_limit_exceeded' ? t('claim.limit') : code === 'rate_limited' ? t('errors.rateLimited') : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (device) return <ClaimProgress deviceId={device.id} />;
  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title={t('claim.title')} subtitle={t('claim.subtitle')} />
      <Card>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {error && <Alert tone="red">{error}</Alert>}
          <Field label={t('claim.hardwareId')} hint={t('claim.hardwareIdHint')}>
            {(p) => <Input {...p} value={form.hardware_id} placeholder="CCK-7Q2M9X4T" pattern="[Cc][Cc][Kk]-[0-9A-Za-z]{8}" required
              className="font-mono" onChange={(e) => setForm({ ...form, hardware_id: e.target.value })} />}
          </Field>
          <Field label={t('claim.code')} hint={t('claim.codeHint')}>
            {(p) => <Input {...p} value={form.claim_code} placeholder="ABCD-EFGH-JKMN" required className="font-mono uppercase"
              autoComplete="off" onChange={(e) => setForm({ ...form, claim_code: e.target.value })} />}
          </Field>
          <Field label={t('devices.label')} hint={t('claim.labelHint')}>
            {(p) => <Input {...p} value={form.label} maxLength={100} onChange={(e) => setForm({ ...form, label: e.target.value })} />}
          </Field>
          <Field label={t('devices.site')}>
            {(p) => (
              <Select {...p} value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
                <option value="">—</option>
                {sites.data?.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            )}
          </Field>
          <Button type="submit" loading={busy}>{t('claim.submit')}</Button>
        </form>
      </Card>
    </div>
  );
}

/** After the claim: waits for the device to provision and report, then its probes can be assigned. */
function ClaimProgress({ deviceId }: { deviceId: string }) {
  const { orgId } = useCurrentOrg();
  const { t } = useI18n();
  const device = useDevice(orgId, deviceId);
  const probes = useProbes(orgId, `?device_id=${deviceId}`);
  const qc = useQueryClient();
  const active = device.data?.status === 'active';
  useEffect(() => {
    const timer = setInterval(() => refreshScoped(qc, orgId, ['device', 'probes']), 5000);
    return () => clearInterval(timer);
  }, [qc, orgId]);
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <PageHeader title={t('claim.claimed')} subtitle={device.data?.hardware_id} />
      <Card title={t('claim.step2')}>
        {active ? <Alert tone="green">{t('claim.online')}</Alert> : (
          <div className="flex items-start gap-3">
            <Spinner />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-800">{t('claim.waiting')}</p>
              <ol className="mt-1 list-decimal pl-5">
                <li>{t('claim.power')}</li>
                <li>{t('claim.network')}</li>
                <li>{t('claim.wait')}</li>
              </ol>
            </div>
          </div>
        )}
      </Card>
      <Card title={t('claim.step3')}>
        {(probes.data?.data.length ?? 0) === 0 ? <p className="text-sm text-slate-500">{t('claim.noProbesYet')}</p>
          : <ProbeTable probes={probes.data!.data} editable />}
      </Card>
      <div className="flex justify-end">
        <LinkButton to={`/o/${orgId}/devices/${deviceId}`}>{t('claim.done')}</LinkButton>
      </div>
    </div>
  );
}
